import { Info } from "lucide-react"
import { useRouter, useSearchParamsCompat } from "@/lib/router-helpers"
import { useTranslation } from 'react-i18next';
import { useBookingStore } from "@/store/booking-store";

type ApiService = {
  id: string
  service_id: string
  name: string
  has_sub_services: boolean
  special_notes: string | null
  sub_services: Array<{ id: string; name: string; active: boolean }>
}

function iconForService(name: string): string {
  const key = name.toLowerCase()
  if (key.includes("representation")) return "/icon/representation.svg"
  if (key.includes("rental")) return "/icon/rental.svg"
  if (key.includes("sales")) return "/icon/sales.svg"
  if (key.includes("plc")) return "/icon/plc.svg"
  if (key.includes("gift")) return "/icon/gift.svg"
  if (key.includes("loan")) return "/icon/loan.svg"
  // if (key.includes("authentication")) return "/icon/authentication.svg"
  return "/icon/other.svg"
}

function descriptionForService(name: string): string {
  const key = name.toLowerCase()
  if (key.includes("representation")) return "Representation related service"
  if (key.includes("rental")) return "Rental related service"
  if (key.includes("sales")) return "Sales related service"
  if (key.includes("plc")) return "PLC related service"
  if (key.includes("gift")) return "Gift related service"
  if (key.includes("loan")) return "Loan related service"
  if (key.includes("authentication")) return "Document authentication related service"
  return "Other related service"
}

export default function ServicesGrid({ services, branchId, branchName, branchSubtitle }: { services: ApiService[]; branchId: string; branchName?: string; branchSubtitle?: string }) {
  const router = useRouter()
  const [searchParams] = useSearchParamsCompat()
  const { t } = useTranslation();
  const setBookingState = useBookingStore((state) => state.setBookingState)
  const mode = useBookingStore((state) => state.mode)

  const sortedServices = [...services].sort((a, b) => {
    const isOtherA = a.name.toLowerCase().includes("other")
    const isOtherB = b.name.toLowerCase().includes("other")
    if (isOtherA && !isOtherB) return 1
    if (!isOtherA && isOtherB) return -1
    return 0
  })

    const handleSelect = (serviceId: string, serviceName: string) => {
    setBookingState({ 
      selectedService: { id: serviceId, name: serviceName },
      selectedSlot: null, // Clear slot when service changes
      selectedDate: null // Clear date when service changes
    })
    
    if (mode === 'reschedule') {
      router.push('/reschedule')
    } else {
      router.push('/booking/schedule')
    }
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4 mt-2 relative z-10">
      {sortedServices.map((s, index) => (
        <button
          key={s.id}
          onClick={() => handleSelect(s.id, s.name)}
          style={{ animationDelay: `${index * 50}ms` }}
          className="group flex flex-col items-center text-center gap-3 px-1 md:px-4 py-6 rounded-2xl border border-gray-200 bg-white shadow-lg shadow-gray-900/5 hover:border-pink-200 hover:shadow-2xl hover:shadow-pink-200/50 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden animate-fade-in-up opacity-0 fill-mode-forwards"
        >
          <div className="absolute inset-0 bg-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          <div className="w-16 h-16 rounded-full bg-pink-50 flex items-center justify-center mb-1 group-hover:scale-110 transition-all duration-300 relative z-10">
            <img src={iconForService(s.name)} alt={s.name} className="w-10 h-10 md:w-11 md:h-11 object-contain drop-shadow-sm" />
          </div>
          
          <div className="relative z-10 flex flex-col gap-1 w-full">
            <p className="text-lg md:text-base font-bold text-gray-900 group-hover:text-pink-600 transition-colors duration-300 line-clamp-2">
              {t(s.name)}
            </p>
            
           

            {s.special_notes !== null && (
              <div className="w-full bg-amber-50 border border-amber-100 rounded-lg p-1 md:p-2 flex items-start gap-1.5 justify-center">
                <Info className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-[13px] md:text-xs font-medium text-amber-700 leading-tight line-clamp-3 ">
                  {t(s.special_notes)}
                </p>
              </div>
            ) }
          </div>
        </button>
      ))}
    </div>
  )
}


